import React, { useCallback, useEffect, useState } from "react";
import isEqual from "lodash/isEqual";
import { WatcherDropdown } from "@adsk/acc-issues-common/components/input/WatcherDropdown";
import { WatcherFieldLabel } from "@adsk/acc-issues-common/components/display/WatcherFieldLabel";
import { useGetLevelOneUserShouldIncludeMemberFn } from "@adsk/acc-issues-common/hooks/data/useGetLevelOneUserShouldIncludeMemberFn";
import { isAttributeEditable } from "../../../utils/data/entityPermissions";
import { DefaultEditFieldInterface } from "./typesForFields";
import { Issue, PermittedAction } from "@adsk/acc-issues-types";
import { useTranslations } from "@adsk/acc-issues-translations";
import { FieldWrapper } from "@adsk/acc-issues-common/components/primitive/FieldWrapper";
import { ConditionalTooltip } from "@adsk/acc-issues-common/components/primitive/ConditionalTooltip";
import Tooltip from "@adsk/alloy-react-tooltip";
import { fetchIssueViewersByIdAction } from "@adsk/acc-issues-core/state/redux/ducks/issuesViewers";
import { useDispatch } from "@adsk/acc-issues-core/state/redux/hooks";
import { useWatcherPropName } from "../../../utils/hooks/watchers/useWatcherPropName";
import { WatcherObject } from "@adsk/acc-issues-types";
import { useGetWatchersValue } from "../../../utils/hooks/watchers/useGetWatchersValue";
import { useTrackWatcherChangeAnalytic } from "@adsk/acc-issues-common/hooks/analytics/useTrackWatcherChangeAnalytic";

const useWatcherIdsFromIssue = (issue: Issue): string[] => {
  const watcherPropName = useWatcherPropName();
  if (watcherPropName === "watchers") {
    return issue.watchers || [];
  } else {
    return issue.watcherObjects?.map(x => x.id) || [];
  }
};

// This Field is similar to the one in IssuesTab/TemplatesConfig/IssueTemplateDetailPanel/detail-panel-fields/...
// In case any necessary change is needed, it would be nice check on the other file as well
export const WatcherField = ({
  issue,
  onChange,
  required,
  viewOnly = false,
}: DefaultEditFieldInterface) => {
  const dispatch = useDispatch();
  const watcherPropName = useWatcherPropName();
  const watcherIdsFromIssue = useWatcherIdsFromIssue(issue);
  const { getWatchersValue } = useGetWatchersValue();
  const isDisabled = !isAttributeEditable(issue, watcherPropName);
  const { T } = useTranslations();
  const { permittedActions } = issue;
  const levelOneUserShouldIncludeMemberFn =
    useGetLevelOneUserShouldIncludeMemberFn();

  const [pendingSelectedWatcherValues, setPendingSelectedWatcherValues] =
    useState<string[] | WatcherObject[]>(issue[watcherPropName] || []);
  const pendingSelectedWatcherIds =
    watcherPropName === "watchers"
      ? (pendingSelectedWatcherValues as string[])
      : (pendingSelectedWatcherValues as WatcherObject[])?.map(x => x.id);

  const watchersDependencyPrimitive = issue[watcherPropName]
    ? JSON.stringify([...watcherIdsFromIssue].sort())
    : "";
  /** this useEffect is needed because the watchers can be updated by the "Assigned to" field */
  useEffect(() => {
    const watcherValues = issue[watcherPropName];
    if (watcherValues) {
      setPendingSelectedWatcherValues(watcherValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchersDependencyPrimitive]);

  const { trackWatcherChange } = useTrackWatcherChangeAnalytic();

  const handleWatcherChange = useCallback(
    (watchers: WatcherObject[]) => {
      const watchersValues = getWatchersValue(watchers);

      // the watchers field may be set to required = true. If this is the case, we do not want to allow clearing.
      // This is meant to not allow removing all watchers if the field is marked as required (with a * in the UI)
      if (required && watchersValues.length === 0) {
        return;
      }

      trackWatcherChange({
        issueId: issue.id,
        watchers,
        pendingSelectedWatcherIds,
      });

      setPendingSelectedWatcherValues(watchersValues);
    },
    [
      getWatchersValue,
      issue.id,
      pendingSelectedWatcherIds,
      required,
      trackWatcherChange,
    ]
  );

  const handleDropdownClose = useCallback(() => {
    const issueWatcherValues = issue[watcherPropName];
    if (!isEqual(pendingSelectedWatcherValues, issueWatcherValues)) {
      onChange(
        { [watcherPropName]: pendingSelectedWatcherValues },
        {
          onSuccess: () => {
            dispatch(
              fetchIssueViewersByIdAction({
                issueId: issue.id,
                forceFetch: true,
              })
            );
          },
        }
      );
    }
  }, [
    dispatch,
    issue,
    onChange,
    pendingSelectedWatcherValues,
    watcherPropName,
  ]);

  const handleTokenRemoved = useCallback(
    (valueToRemove: string) => {
      onChange(
        {
          [watcherPropName]: pendingSelectedWatcherValues.filter(option =>
            typeof option === "string"
              ? option !== valueToRemove
              : option.id !== valueToRemove
          ),
        },
        {
          onSuccess: () => {
            dispatch(
              fetchIssueViewersByIdAction({
                issueId: issue.id,
                forceFetch: true,
              })
            );
          },
        }
      );
    },
    [dispatch, issue, onChange, pendingSelectedWatcherValues, watcherPropName]
  );

  return (
    <FieldWrapper
      label={<WatcherFieldLabel />}
      required={required}
      testId="watchers-field"
    >
      <ConditionalTooltip
        condition={isDisabled && !viewOnly}
        placement={Tooltip.PLACEMENTS.BOTTOM}
        style={{
          maxWidth: 200,
        }}
        content={T.translate("editable.noPermissions")}
      >
        <WatcherDropdown
          shouldIncludeMemberFn={
            !permittedActions.includes(PermittedAction.AssignAll)
              ? levelOneUserShouldIncludeMemberFn
              : undefined
          }
          selectedWatcherIds={pendingSelectedWatcherIds}
          onChange={handleWatcherChange}
          onClose={handleDropdownClose}
          onTokenRemoved={handleTokenRemoved}
          clearable={!required}
          viewOnly={viewOnly || isDisabled}
          shouldIncludeEmpty={false}
        />
      </ConditionalTooltip>
    </FieldWrapper>
  );
};
